import React, { useEffect, useState, useRef } from 'react';
import {
  withStyles, FormControl, FormLabel, FormGroup, Checkbox,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  Formik, Form, Field, ErrorMessage,
} from 'formik';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import Footer from '~/pages/Manager/Footer';
import PF2MTable from '~/components/PF2MTable';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import {
  getEquipmentTypes,
  getEquipmentsGroups,
  getDrillholeTypes,
  addDrillholeType,
  setDrillholeTypeValue,
  resetDrillholeTypes,
  updateDrillholeTypes,
  getOperationTypes,
} from '~/store/manager/actions';
import PF2MAddButton from '~/components/PF2MAddButton';
import { sortByProperties } from '~/utils';
import PF2MAutocomplete from '~/components/PF2MAutocomplete';

const PERFORATOR_EQUIP_IDS = [4, 11, 16, 17, 18, 19];

const DrillholeTypeTab = ({ classes }) => {
  const equipTypes = useSelector(state => state.manager.equipmentTypes);
  const equipGroups = useSelector(state => state.manager.equipmentsGroups);
  const drillholeTypes = useSelector(state => state.manager.drillholeTypes);
  const isDrillholeTypesDirty = useSelector(state => state.manager.isDrillholeTypesDirty);
  const operationTypes = useSelector(state => state.manager.operationTypes
    .filter(e => e.active === 1)
    .filter(e => PERFORATOR_EQUIP_IDS.includes(e.id_equipment_type)), shallowEqual);
  const [selectedEquipType, setSelectedEquipType] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [selectedEquipGroup, setSelectedEquipGroup] = useState(0);
  const [drillholeTypeInactive, setDrillholeTypeInactive] = useState(false);

  const dispatch = useDispatch();
  const formikRef = useRef();
  const { t: translate } = useTranslation();

  useEffect(() => {
    async function fetchData() {
      dispatch(getDrillholeTypes(true));
      dispatch(getEquipmentTypes());
      dispatch(getEquipmentsGroups());
      dispatch(getOperationTypes(true));
    }
    fetchData();
  }, [dispatch]);

  const renderOperationTypes = (
    row,
    opsTypes,
    dp,
    setValue,
  ) => {
    const formatOperationType = ({ id, name }) => ({ value: id, label: name });

    const availableOptions = opsTypes
      .filter(ot => ot.id_equipment_type === row.equipment_type_id)
      .map(formatOperationType);

    const selectedValues = (row.operation_types || []).map(formatOperationType);
    const hasError = row.operation_types.length < 1;
    return (
      <>
        <div style={{ minWidth: 200 }}>
          <PF2MAutocomplete
            title=""
            placeholder={translate('common:Select')}
            options={availableOptions}
            className={classnames(
              hasError ? classes.fieldError : classes.field,
            )}
            value={selectedValues}
            onChange={(event, newValue) => {
              const mapped = newValue?.map(({ value, label }) => ({
                id: value,
                name: label,
              })) || [];

              const updatedRow = {
                ...row,
                operation_types: mapped,
                update_status: row.update_status || 'U',
              };

              dp(
                setValue(updatedRow, 'operation_types', mapped),
              );
            }}
            multiple
            limitTags={1}
          />
        </div>
        {hasError && (
          <errorMessage className={classes.errorMessage}>
            {translate('validation:RequiredField')}
          </errorMessage>
        )}
      </>
    );
  };

  const columns = [
    {
      field: 'equipment_type_name',
      title: translate('common:EquipmentType'),
    },
    {
      field: 'equipment_group_name',
      title: translate('common:EquipmentGroup'),
    },
    {
      field: 'name',
      title: translate('common:Name'),
      width: '100%',
      editable: true,
    },
    {
      field: 'active',
      title: 'STATUS',
      editable: true,
    },
    {
      field: 'operation_types',
      title: translate('common:OperationType'),
      editable: true,
      renderCustomComponent: row => renderOperationTypes(
        row,
        operationTypes,
        dispatch,
        setDrillholeTypeValue,
      ),
    },
  ];

  const hasTableErrors = () => drillholeTypes.some(
    row => (row.update_status === 'I' || row.update_status === 'U')
          && (!row.operation_types || row.operation_types.length === 0),
  );

  const canSave = () => isDrillholeTypesDirty && !hasTableErrors();

  const getNameById = (id, list) => {
    const { name = '' } = list.find(r => r.id === id) || {};
    return name;
  };

  const renderHeader = () => (
    <Formik
      ref={formikRef}
      initialValues={{
        name: '', equipType: 0, equipGroup: 0, operationTypes: [],
      }}
      validate={(values) => {
        const errors = {};
        const {
          name, equipType, equipGroup, operationTypes: formOperationTypes,
        } = values;

        if (!name) {
          errors.name = translate('validation:RequiredField');
        }

        if (!equipType) {
          errors.equipType = translate('validation:RequiredField');
        }

        if (!equipGroup) {
          errors.equipGroup = translate('validation:RequiredField');
        }

        if (!formOperationTypes || formOperationTypes.length === 0) {
          errors.operationTypes = translate('validation:RequiredField');
        }

        const existingType = drillholeTypes
          .some(d => (
            String(d.name).toLowerCase() === String(name).toLowerCase()
          && d.equipment_group_id === equipGroup
          ));
        if (existingType) {
          errors.name = translate('validation:AlreadyRegisteredName');
        }

        return errors;
      }}
      onSubmit={async (values, { resetForm }) => {
        const newValues = {
          name: values.name,
          equipment_group_id: values.equipGroup,
          equipment_type_id: values.equipType,
          equipment_group_name: getNameById(values.equipGroup, equipGroups),
          equipment_type_name: getNameById(values.equipType, equipTypes),
          operation_types: values.operationTypes.map(operationId => ({
            id: operationId,
            name: getNameById(operationId, operationTypes),
          })),
          update_status: 'I',
        };

        dispatch(addDrillholeType(newValues));
        resetForm();
      }}
    >
      {({
        isSubmitting,
        errors,
        isValid,
      }) => (
        <Form>
          <div style={{ display: 'flex', marginBottom: 15, minHeight: 75 }}>
            <FormControl>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:EquipmentType')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="equipType"
                  render={({ field, form }) => (
                    <>
                      <PF2MSearchSelectOutlined
                        {...field}
                        placeholder={null}
                        className={classnames(
                          form.errors[field.name] ? classes.fieldError : classes.field,
                        )}
                        onChange={async (e) => {
                          const selectedValue = e.target.value;
                          form.setFieldValue('equipType', selectedValue);
                          setSelectedEquipType(selectedValue);

                          form.setFieldValue('equipGroup', 0);
                          setSelectedEquipGroup(0);

                          if (selectedValue && selectedValue !== 0) {
                            const availableOperationTypes = operationTypes
                              .filter(ot => ot.id_equipment_type === selectedValue)
                              .map(ot => ot.id);

                            form.setFieldValue('operationTypes', availableOperationTypes);
                          } else {
                            form.setFieldValue('operationTypes', []);
                          }
                        }}
                      >
                        {[{ value: 0, label: translate('common:Select') }]
                          .concat(...equipTypes
                            .filter(e => ([4, 11, 16, 17, 18, 19].includes(e.id)))
                            .map(e => ({ value: e.id, label: e.name })))}
                      </PF2MSearchSelectOutlined>
                      {form.errors.equipType && (
                        <span className={classes.errorMessage}>
                          {form.errors.equipType}
                        </span>
                      )}
                    </>
                  )}
                />
              </FormGroup>
            </FormControl>
            <FormControl style={{ marginLeft: 10 }}>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:EquipmentGroup')}`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="equipGroup"
                  render={({ field, form }) => (
                    <PF2MSearchSelectOutlined
                      {...field}
                      placeholder={null}
                      className={classnames(
                        form.error
                        && form.error[classes.field] ? classes.fieldError : classes.field,
                      )}
                      onChange={async (e) => {
                        form.setFieldValue('equipGroup', e.target.value);
                        setSelectedEquipGroup(e.target.value);
                      }}
                    >
                      {[{ value: 0, label: translate('common:Select') }]
                        .concat(...equipGroups
                          .filter(s => s.id_equipament === selectedEquipType)
                          .map(e => ({ value: e.id, label: e.name })))}
                    </PF2MSearchSelectOutlined>
                  )}
                />
                <ErrorMessage
                  name="equipGroup"
                  component="span"
                  className={classes.errorMessage}
                />
              </FormGroup>
            </FormControl>
            <FormControl style={{ marginLeft: 10, minWidth: 180 }}>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:OperationType')}`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="operationTypes"
                  render={({ field, form }) => {
                    const options = operationTypes
                      .filter(ot => ot.id_equipment_type === selectedEquipType)
                      .map(ot => ({ value: ot.id, label: ot.name }));

                    return (
                      <div style={{
                        width: '202px',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                      }}
                      >
                        <PF2MAutocomplete
                          title=""
                          placeholder={translate('common:Select')}
                          options={options}
                          disabled={false}
                          className={classnames(
                            form.errors[field.name] ? classes.fieldError : classes.field,
                          )}
                          value={options.filter(opt => (Array.isArray(field.value)
                            ? field.value.includes(opt.value)
                            : field.value === opt.value))}
                          onChange={(event, newValue) => {
                            form.setFieldValue(
                              'operationTypes',
                              newValue ? newValue.map(item => item.value) : [],
                            );
                          }}
                          limitTags={1}
                          style={{
                            width: '100%',
                            flex: 1,
                            '& .MuiAutocomplete-inputRoot': {
                              flexWrap: 'wrap',
                              minHeight: '56px',
                              padding: '6px 12px',
                              alignItems: 'flex-start',
                            },
                            '& .MuiAutocomplete-tag': {
                              margin: '2px',
                              maxWidth: 'calc(50% - 4px)',
                            },
                          }}
                        />
                        {form.errors.operationTypes && (
                          <span className={classes.errorMessage}>
                            {form.errors.operationTypes}
                          </span>
                        )}
                      </div>
                    );
                  }}
                />
              </FormGroup>
            </FormControl>
            <FormControl className={classes.classGroupName} style={{ marginLeft: 10 }}>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:HoleType')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  type="text"
                  name="name"
                  className={
                    classnames('form-control', errors.name ? classes.fieldError : classes.field)
                  }
                  style={{ width: '100%' }}
                />
                <ErrorMessage
                  name="name"
                  component="span"
                  className={classes.errorMessage}
                />
              </FormGroup>
            </FormControl>
            <div style={{ marginTop: 12 }}>
              <PF2MAddButton disabled={isSubmitting || !isValid} />
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );

  return (
    <div className={classes.tabContainer}>
      {renderHeader()}
      <div style={{ display: 'flex', marginBottom: 1, minHeight: 5 }}>
        <div>
          <Checkbox
            checked={drillholeTypeInactive}
            onChange={() => setDrillholeTypeInactive(!drillholeTypeInactive)}
          />
        </div>
        <div>
          <p>{translate('common:ShowInactive')}</p>
        </div>
      </div>
      <PF2MTable
        data={sortByProperties(
          drillholeTypes,
          [{ prop: 'name', type: 'string' }],
        ).filter(d => (drillholeTypeInactive || d.active))
        }
        columns={columns}
        updateItem={(row, field, value) => dispatch(setDrillholeTypeValue(row, field, value))}
      />
      <Footer
        isDirty={isDrillholeTypesDirty}
        discard={() => {
          dispatch(resetDrillholeTypes());

          if (formikRef) {
            formikRef.current.resetForm({ name: '' });
          }
        }}
        sendData={() => {
          dispatch(updateDrillholeTypes(drillholeTypes));
          setDrillholeTypeInactive(false);
        }}
        canSave={canSave()}
      />
    </div>
  );
};

DrillholeTypeTab.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(DrillholeTypeTab);
